import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getSocket, 
  sendMultiplayerGameUpdate, 
  onMultiplayerGameUpdate,
  onPlayerJoined
} from '../utils/socketUtils';
import HangmanDisplay from './HangmanDisplay';
import Word from '../components/Word';
import Keyboard from './Keyboard';
import GameOver from './GameOver';
import LoadingSpinner from './LoadingSpinner';
import '../styles/Game.css';
import '../styles/MultiplayerGame.css';

function MultiplayerGame() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  
  // Game state
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [word, setWord] = useState('');
  const [guessedLetters, setGuessedLetters] = useState([]);
  const [wrongGuesses, setWrongGuesses] = useState([]);
  const [gameStatus, setGameStatus] = useState('playing'); // 'playing', 'won', 'lost'
  
  // Multiplayer specific state
  const [gameData, setGameData] = useState(null);
  const [opponent, setOpponent] = useState(null);
  const [opponentGuesses, setOpponentGuesses] = useState([]);
  const [opponentWrongGuesses, setOpponentWrongGuesses] = useState([]);
  const [opponentStatus, setOpponentStatus] = useState('playing');
  const [isPlayerTurn, setIsPlayerTurn] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(20);
  const [gameStarted, setGameStarted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  
  // Add game timer (100 seconds)
  const [gameTimeRemaining, setGameTimeRemaining] = useState(100);
  const gameTimerRef = useRef(null);

  // Get user data from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    } else {
      // Generate random username if not available
      const randomUsername = 'Player_' + Math.floor(Math.random() * 10000);
      const newUser = { username: randomUsername, id: Date.now().toString() };
      localStorage.setItem('user', JSON.stringify(newUser));
      setUser(newUser);
    }
  }, []);

  // Add event listener for pausing gameplay
  useEffect(() => {
    const handlePauseGameplay = (event) => {
      setIsPaused(event.detail.paused);
    };

    window.addEventListener('pauseGameplay', handlePauseGameplay);
    
    return () => {
      window.removeEventListener('pauseGameplay', handlePauseGameplay);
    };
  }, []);

  // Join game room when component loads
  useEffect(() => {
    if (!user || !gameId) return;

    const socket = getSocket();
    
    // Join the specific game room
    socket.emit('join_game', { 
      userId: user.id, 
      username: user.username, 
      gameId: gameId
    });

    // Listen for game data updates
    const removeUpdateListener = onMultiplayerGameUpdate((data) => {
      if (data.gameId !== gameId) return;
      
      setGameData(data);
      setWord(data.word);
      
      // Set initial turn
      if (!gameStarted && data.players.length === 2) {
        setGameStarted(true);
        setIsPlayerTurn(data.currentTurn === user.id);
        
        // Find opponent
        const opponentData = data.players.find(p => p.id !== user.id);
        if (opponentData) {
          setOpponent(opponentData);
        }
      }
      
      // Update opponent's guesses
      if (data.playerStates) {
        const opponentState = data.playerStates[opponent?.id];
        if (opponentState) {
          setOpponentGuesses(opponentState.guessedLetters || []);
          setOpponentWrongGuesses(opponentState.wrongGuesses || []);
          setOpponentStatus(opponentState.status || 'playing');
        }
        
        // Update turn
        setIsPlayerTurn(data.currentTurn === user.id);
        
        // Reset timer when turn changes
        if (data.currentTurn === user.id) {
          setTimerSeconds(20);
        }
      }
      
      setLoading(false);
    });

    // Listen for player joined event
    const removePlayerJoinedListener = onPlayerJoined((data) => {
      if (data.gameId !== gameId) return;
      
      // If another player joined, update opponent
      if (data.user.id !== user.id) {
        setOpponent(data.user);
      }
    });

    return () => {
      removeUpdateListener();
      removePlayerJoinedListener();
    };
  }, [gameId, user, gameStarted, opponent]);

  // Timer for turn-based gameplay
  useEffect(() => {
    if (!gameStarted || !isPlayerTurn || gameStatus !== 'playing' || isPaused) return;
    
    const timer = setInterval(() => {
      setTimerSeconds(prev => {
        if (prev <= 1) {
          // Time's up - skip turn
          handleSkipTurn();
          return 20; // Reset timer
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [gameStarted, isPlayerTurn, gameStatus, isPaused]);

  // Add keyboard event listener for letter input
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't process keyboard input if the game is paused (user is searching)
      if (isPaused) return;
      
      // Only handle letter keys when it's the player's turn
      if (/^[a-zA-Z]$/.test(e.key) && isPlayerTurn && gameStatus === 'playing') {
        const letter = e.key.toUpperCase();
        if (!guessedLetters.includes(letter)) {
          handleGuess(letter);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [guessedLetters, isPlayerTurn, gameStatus, handleGuess, isPaused]);

  // Handle letter guess
  const handleGuess = useCallback((letter) => {
    if (!isPlayerTurn || gameStatus !== 'playing') return;
    
    const updatedGuessedLetters = [...guessedLetters, letter];
    setGuessedLetters(updatedGuessedLetters);
    
    // Check if letter is in the word
    if (!word.includes(letter)) {
      const updatedWrongGuesses = [...wrongGuesses, letter];
      setWrongGuesses(updatedWrongGuesses);
      
      // Check if game is lost
      if (updatedWrongGuesses.length >= 6) {
        setGameStatus('lost');
        
        // Send game update
        sendMultiplayerGameUpdate({
          gameId,
          userId: user.id,
          guessedLetter: letter,
          wrongGuess: true,
          guessedLetters: updatedGuessedLetters,
          wrongGuesses: updatedWrongGuesses,
          status: 'lost'
        });
        
        return;
      }
    } else {
      // Check if game is won
      const isWordGuessed = [...word].every(char => 
        updatedGuessedLetters.includes(char) || char === ' '
      );
      
      if (isWordGuessed) {
        setGameStatus('won');
        
        // Send game update
        sendMultiplayerGameUpdate({
          gameId,
          userId: user.id,
          guessedLetter: letter,
          wrongGuess: false,
          guessedLetters: updatedGuessedLetters,
          wrongGuesses,
          status: 'won'
        });
        
        return;
      }
    }
    
    // Send game update and pass turn
    sendMultiplayerGameUpdate({
      gameId,
      userId: user.id,
      guessedLetter: letter,
      wrongGuess: !word.includes(letter),
      guessedLetters: updatedGuessedLetters,
      wrongGuesses: !word.includes(letter) ? [...wrongGuesses, letter] : wrongGuesses,
      passTurn: true
    });
    
    // Pass turn
    setIsPlayerTurn(false);
  }, [gameId, user, word, guessedLetters, wrongGuesses, isPlayerTurn, gameStatus]);

  // Handle skip turn
  const handleSkipTurn = () => {
    if (!isPlayerTurn) return;
    
    // Send turn skip update
    sendMultiplayerGameUpdate({
      gameId,
      userId: user.id,
      passTurn: true,
      timeExpired: true
    });
    
    setIsPlayerTurn(false);
  };

  // Handle play again
  const handlePlayAgain = () => {
    navigate('/');
  };

  // Add game timer effect
  useEffect(() => {
    if (!gameStarted || gameStatus !== 'playing' || isPaused) return;
    
    // Clear any existing timer
    if (gameTimerRef.current) {
      clearInterval(gameTimerRef.current);
    }
    
    // Set up new game timer
    gameTimerRef.current = setInterval(() => {
      setGameTimeRemaining(prevTime => {
        if (prevTime <= 1) {
          // Time's up - end the game
          clearInterval(gameTimerRef.current);
          setGameStatus('lost');
          
          // Notify opponent
          sendMultiplayerGameUpdate({
            gameId,
            userId: user?.id,
            action: 'timeout',
            result: 'lost'
          });
          
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
    
    // Cleanup timer on effect cleanup
    return () => {
      if (gameTimerRef.current) {
        clearInterval(gameTimerRef.current);
      }
    };
  }, [gameStarted, gameStatus, isPaused, gameId, user]);

  // Cleanup all timers when component unmounts
  useEffect(() => {
    return () => {
      if (gameTimerRef.current) {
        clearInterval(gameTimerRef.current);
      }
    };
  }, []);

  if (loading) {
    return <LoadingSpinner message="Loading multiplayer game..." />;
  }

  return (
    <div className="game-container multiplayer-game">
      <div className="game-header">
        <h1>Multiplayer Hangman</h1>
        {opponent && (
          <div className="opponent-info">
            <span className="vs-text">VS</span>
            <span className="opponent-name">{opponent.username}</span>
          </div>
        )}
      </div>
      
      {/* Add game timer display */}
      <div className="timer-container">
        <div className={`timer ${gameTimeRemaining <= 20 ? 'timer-warning' : ''}`}>
          Game time: {gameTimeRemaining} seconds
        </div>
      </div>
      
      {gameStarted ? (
        <>
          <div className="players-container">
            <div className={`player-section ${isPlayerTurn ? 'current-turn' : ''}`}>
              <h3>{user.username} (You)</h3>
              <HangmanDisplay wrongGuesses={wrongGuesses.length} />
              <div className="game-status">
                {gameStatus === 'playing' ? (
                  isPlayerTurn ? (
                    <div className="turn-timer">
                      Your turn - {timerSeconds}s remaining
                    </div>
                  ) : (
                    <div className="waiting-text">Waiting for opponent...</div>
                  )
                ) : (
                  <div className={`result-text ${gameStatus}`}>
                    {gameStatus === 'won' ? 'You won!' : 'You lost!'}
                  </div>
                )}
              </div>
            </div>

            {opponent && (
              <div className={`player-section ${!isPlayerTurn ? 'current-turn' : ''}`}>
                <h3>{opponent.username}</h3>
                <HangmanDisplay wrongGuesses={opponentWrongGuesses.length} />
                <div className="game-status">
                  {opponentStatus === 'playing' ? (
                    !isPlayerTurn ? (
                      <div className="turn-text">Their turn</div>
                    ) : (
                      <div className="waiting-text">Waiting...</div>
                    )
                  ) : (
                    <div className={`result-text ${opponentStatus}`}>
                      {opponentStatus === 'won' ? 'They won!' : 'They lost!'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="shared-word-section">
            <Word word={word} guessedLetters={guessedLetters} />
          </div>

          {(gameStatus === 'playing' && isPlayerTurn) && (
            <div className="keyboard-section">
              <Keyboard 
                guessedLetters={guessedLetters} 
                onGuess={handleGuess} 
                disabled={!isPlayerTurn || gameStatus !== 'playing'}
              />
              <button 
                className="skip-turn-btn" 
                onClick={handleSkipTurn}
              >
                Skip Turn
              </button>
            </div>
          )}
          
          {(gameStatus !== 'playing' || opponentStatus !== 'playing') && (
            <GameOver 
              gameStatus={gameStatus} 
              word={word} 
              onPlayAgain={handlePlayAgain} 
            />
          )}
        </>
      ) : (
        <div className="waiting-players">
          <h2>Waiting for opponent to join...</h2>
          <p>Game ID: {gameId}</p>
          <p>Share this URL with a friend to play together.</p>
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
}

export default MultiplayerGame; 